import { useMemo } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";

import {
  INITIAL_CATEGORY_ENTITY,
  useForeCategoryEntityQuery,
} from "services/api";

import LogoTainan from "images/logo-tainan-2.svg";
import IconTotal from "images/icon-total.svg";
import IconTotalO from "images/icon-total-o.svg";
import { useCategoryContext } from "features/forestage/Categories/CategoryContext";

interface SideMenuProps {
  showSideNav: boolean;
  setShowSideNav: (value: boolean) => void;
}

const SideMenu = ({ showSideNav, setShowSideNav }: SideMenuProps) => {
  const { data: categoryEntity = INITIAL_CATEGORY_ENTITY } =
    useForeCategoryEntityQuery();

  const activatedCategory = useCategoryContext();

  const totalCount = useMemo(
    () =>
      categoryEntity.ids.reduce(
        (prev, id) => (prev += categoryEntity.entities[id].chartCount),
        0
      ),

    [categoryEntity]
  );

  return (
    <aside
      className={classNames(
        "scrollbarHide fixed top-0 left-0 z-10 h-full w-full bg-grandma-400 pt-[100px] print:hidden md:sticky md:top-24 md:ml-8 md:mb-16 md:block md:max-h-[80vh] md:w-48 md:overflow-y-auto md:overflow-x-hidden md:bg-transparent md:p-0 lg:top-32",
        { "hidden md:block": !showSideNav }
      )}
    >
      <div className="m-auto flex h-[100%] w-[288px] flex-col justify-between md:h-auto md:w-auto">
        <ul className="flex flex-wrap justify-between pb-5 md:block">
          <li className="mb-8 md:mb-[0.7rem]">
            <Link to={"/"} onClick={() => setShowSideNav(false)}>
              <div
                className={classNames(
                  "flex h-24 w-[130px] items-center justify-between rounded-2xl bg-grandma-300 p-4 drop-shadow-sm md:shadow-lg lg:w-[150px]",
                  {
                    "md:bg-grandma-300": activatedCategory === undefined,
                    "md:bg-white": activatedCategory !== undefined,
                  }
                )}
              >
                <div className="">
                  <img
                    className={classNames(
                      "h-[42px] w-[50px] md:h-[50px] brightness-[100]",
                      {
                        "md:filter-none": activatedCategory !== undefined,
                      }
                    )}
                    src={
                      activatedCategory === undefined ? IconTotalO : IconTotal
                    }
                    alt=""
                    draggable="false"
                  />
                </div>
                <div className="flex-inline flex-col justify-between text-right">
                  <h3
                    className={classNames(
                      "w-14 overflow-hidden text-ellipsis text-lg font-bold text-white",
                      {
                        "md:text-black": activatedCategory !== undefined,
                      }
                    )}
                  >
                    全部
                  </h3>
                  <span
                    className={classNames(
                      "border-b-[3px] border-white px-0.5 pb-0.5 text-xs font-bold text-white",
                      {
                        "md:border-grandma-300 md:text-grandma-300":
                          activatedCategory !== undefined,
                      }
                    )}
                  >
                    {totalCount}
                  </span>
                </div>
              </div>
            </Link>
          </li>
          {categoryEntity.ids.map((id) => {
            const item = categoryEntity.entities[id];
            const isActive = id === activatedCategory;

            return (
              <li key={item.id} className="mb-8 md:mb-[0.7rem]">
                <Link
                  to={`categories/${item.url || item.id}`}
                  onClick={() => setShowSideNav(false)}
                >
                  <div
                    className={classNames(
                      "flex h-24 w-[130px] items-center justify-between rounded-2xl bg-grandma-300 p-4 drop-shadow-sm md:shadow-lg lg:w-[150px]",
                      {
                        "md:bg-grandma-300": isActive,
                        "md:bg-white": !isActive,
                      }
                    )}
                  >
                    <div className="">
                      <img
                        className={classNames(
                          "h-[42px] w-[50px] md:h-[50px] brightness-[100]",
                          {
                            "md:filter-none": !isActive,
                          }
                        )}
                        src={item.icon}
                        alt=""
                        draggable="false"
                      />
                    </div>
                    <div className="flex-inline flex-col justify-between text-right">
                      <h3
                        className={classNames(
                          "w-14 overflow-hidden text-ellipsis text-lg font-bold text-white",
                          {
                            "md:text-black": !isActive,
                          }
                        )}
                      >
                        {item.name}
                      </h3>
                      <span
                        className={classNames(
                          "border-b-[3px] border-white px-0.5 pb-0.5 text-xs font-bold text-white",
                          {
                            "md:border-grandma-300 md:text-grandma-300":
                              !isActive,
                          }
                        )}
                      >
                        {item.chartCount}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
        <div className="black flex items-center justify-center pb-12 text-[12px] text-white md:hidden">
          <div className="mt-3 mr-3">Copyright © 2022 台南市政府</div>
          <img src={LogoTainan} alt="" draggable="false" />
        </div>
      </div>
    </aside>
  );
};

export default SideMenu;
